/**
 * Renders LLM-emitted markdown into sanitized HTML with syntax
 * highlighting and copy-to-clipboard buttons on code blocks.
 *
 * Streaming-safe: re-parses on every source change. `marked` +
 * curated hljs languages are microsecond-fast for typical chat
 * turns. DOMPurify scrubs script tags, event handlers,
 * javascript: URLs, etc. — required because LLM output is
 * untrusted.
 *
 * LaTeX math (\[…\], $$…$$, \(…\)) is rendered via KaTeX in a marked
 * extension — see ./math. Out of scope today: mermaid diagrams.
 */
import DOMPurify from "dompurify";
import "katex/dist/katex.min.css";
import hljs from "highlight.js/lib/core";
import bash from "highlight.js/lib/languages/bash";
import css from "highlight.js/lib/languages/css";
import dockerfile from "highlight.js/lib/languages/dockerfile";
import go from "highlight.js/lib/languages/go";
import javascript from "highlight.js/lib/languages/javascript";
import json from "highlight.js/lib/languages/json";
import markdown from "highlight.js/lib/languages/markdown";
import plaintext from "highlight.js/lib/languages/plaintext";
import python from "highlight.js/lib/languages/python";
import rust from "highlight.js/lib/languages/rust";
import sql from "highlight.js/lib/languages/sql";
import typescript from "highlight.js/lib/languages/typescript";
import xml from "highlight.js/lib/languages/xml";
import yaml from "highlight.js/lib/languages/yaml";
import hljsCss from "highlight.js/styles/github-dark-dimmed.css?inline";
import { Marked, Renderer } from "marked";
import { type Component, createMemo, onMount } from "solid-js";

import { wrapNumericBadges } from "./badges";
import { mathExtension, resetMath, substituteMath } from "./math";
import { SAFE_URI_REGEXP } from "./safeUri";

hljs.registerLanguage("bash", bash);
hljs.registerLanguage("css", css);
hljs.registerLanguage("dockerfile", dockerfile);
hljs.registerLanguage("go", go);
hljs.registerLanguage("html", xml);
hljs.registerLanguage("javascript", javascript);
hljs.registerLanguage("json", json);
hljs.registerLanguage("js", javascript);
hljs.registerLanguage("markdown", markdown);
hljs.registerLanguage("md", markdown);
hljs.registerLanguage("plaintext", plaintext);
hljs.registerLanguage("python", python);
hljs.registerLanguage("py", python);
hljs.registerLanguage("rust", rust);
hljs.registerLanguage("rs", rust);
hljs.registerLanguage("shell", bash);
hljs.registerLanguage("sh", bash);
hljs.registerLanguage("sql", sql);
hljs.registerLanguage("ts", typescript);
hljs.registerLanguage("typescript", typescript);
hljs.registerLanguage("xml", xml);
hljs.registerLanguage("yaml", yaml);
hljs.registerLanguage("yml", yaml);

function ensureHljsStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById("hljs-styles")) return;
  const style = document.createElement("style");
  style.id = "hljs-styles";
  style.textContent = hljsCss;
  document.head.appendChild(style);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Defensive — fenced lang tokens from LLM output can be junk.
function safeLang(lang: string | undefined): string | null {
  if (!lang) return null;
  const cleaned = lang.toLowerCase().match(/^[a-z0-9_+-]{1,32}$/);
  return cleaned ? cleaned[0] : null;
}

function highlight(code: string, lang: string | null): string {
  if (lang && hljs.getLanguage(lang)) {
    try {
      return hljs.highlight(code, { language: lang, ignoreIllegals: true })
        .value;
    } catch {
      // fall through to escaped plain text
    }
  }
  return escapeHtml(code);
}

const renderer = new Renderer();
renderer.code = ({ text, lang }) => {
  const safe = safeLang(lang);
  const inner = highlight(text, safe);
  const langClass = safe ? ` language-${safe}` : "";
  const label = escapeHtml(safe ?? "code");
  return `<div class="md-pre"><div class="md-pre-head"><span>${label}</span><button class="md-copy" type="button">copy</button></div><pre><code class="hljs${langClass}">${inner}</code></pre></div>`;
};

const marked = new Marked({ gfm: true, breaks: true });
marked.use({ renderer });
marked.use(mathExtension);

interface Props {
  source: string;
}

const MarkdownView: Component<Props> = (props) => {
  onMount(ensureHljsStyles);
  let host: HTMLDivElement | undefined;

  const html = createMemo(() => {
    // Order is load-bearing: the math extension renders into a buffer and
    // emits inert placeholders; wrapNumericBadges runs on those (can't
    // corrupt KaTeX markup); substituteMath swaps in the real KaTeX HTML
    // just before sanitization. See ./math for why.
    resetMath();
    const raw = marked.parse(props.source, { async: false }) as string;
    const withBadges = wrapNumericBadges(raw);
    const withMath = substituteMath(withBadges);
    return DOMPurify.sanitize(withMath, {
      ADD_ATTR: ["target", "rel"],
      // Extend DOMPurify's default URI allow-list with raster
      // data:image/* base64 schemes. Required so /image (Gemini)
      // responses render — the BE emits `![…](data:image/png;base64,…)`
      // and the default regex strips it, leaving only the alt text.
      // svg+xml is intentionally excluded (carries inline scripts).
      ALLOWED_URI_REGEXP: SAFE_URI_REGEXP,
    });
  });

  // Event delegation for copy buttons. We can't attach inline
  // onclick because DOMPurify strips event-handler attributes,
  // and re-parsing on every token would re-bind anyway.
  const handleClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.classList.contains("md-copy")) return;
    const pre = target.closest(".md-pre")?.querySelector("pre");
    if (!pre) return;
    void navigator.clipboard.writeText(pre.textContent ?? "");
    const original = target.textContent;
    target.textContent = "copied";
    target.classList.add("md-copy-ok");
    window.setTimeout(() => {
      target.textContent = original;
      target.classList.remove("md-copy-ok");
    }, 1200);
  };

  // Keyboard handler is a no-op routed through the same delegation:
  // native <button> descendants already fire `click` on Enter/Space,
  // so they hit handleClick directly. This satisfies a11y lints that
  // require onClick to be paired with a keyboard listener.
  const handleKeyDown = (_e: KeyboardEvent) => {};

  return (
    <div
      class="md-view"
      ref={host}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      innerHTML={html()}
    />
  );
};

export default MarkdownView;
